import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { getUrl, Resource, ResourceFactory } from '@ngxp/rest';
import { ApiRootLinkRel } from '@schir-int-client/api-root';
import { Observable } from 'rxjs';
import { KontaktLinkRel, VerfahrenKontaktLinkRel } from './adressverwaltung.linkrel';
import {
	Kontakt,
	KontaktInVerfahrenListResource,
	KontaktListResource,
	KontaktResource,
	VerfahrenKontakt,
	VerfahrenKontaktResource,
} from './adressverwaltung.model';
import { VerfahrenLinkRel } from '../../../verfahren-shared/src/lib/verfahren.linkrel';
import { VerfahrenListResource, VerfahrenResource } from '../../../verfahren-shared/src/lib/verfahren.model';

@Injectable({ providedIn: 'root' })
export class AdressverwaltungService {
	@Output() adressatenColDisplayed = new EventEmitter<boolean>();

	constructor(
		private resourceFactory: ResourceFactory,
		private httpClient: HttpClient
	) {}

	public search(
		rootResource: Resource,
		condition: string,
		filter: string,
		limit: number
	): Observable<KontaktListResource> {
		const params: HttpParams = new HttpParams().set('in', filter).set('searchFor', condition).set('limit', limit);

		const url: string = getUrl(rootResource, ApiRootLinkRel.KONTAKTS);
		return this.httpClient.get<KontaktListResource>(url, { params });
	}

	public get(kontaktUri: string): Observable<KontaktResource> {
		return this.resourceFactory.fromId(kontaktUri).get();
	}

	public create(rootResource: Resource, kontakt: Kontakt): Observable<KontaktResource> {
		return this.resourceFactory.from(rootResource).post(ApiRootLinkRel.KONTAKTS, kontakt);
	}

	public delete(kontakt: KontaktResource): Observable<KontaktListResource> {
		return this.resourceFactory.from(kontakt).post(KontaktLinkRel.DELETE);
	}

	public update(kontaktResource: KontaktResource, newKontakt: Kontakt): Observable<KontaktResource> {
		const updated = { ...(<Kontakt>kontaktResource), ...newKontakt };
		return this.resourceFactory.from(kontaktResource).put(KontaktLinkRel.EDIT, updated);
	}

	public setKontaktAssignment(
		verfahren: VerfahrenResource,
		verfahrenKontakt: VerfahrenKontakt
	): Observable<VerfahrenResource> {
		const kontaktId: string = String(verfahrenKontakt.kontaktId);
		const url: string = getUrl(verfahren, VerfahrenLinkRel.SET_KONTAKT_ASSIGNED_IN_KATEGORIEN).replace(
			'{kontaktId}',
			kontaktId
		);
		return this.httpClient.put<VerfahrenResource>(url, JSON.stringify(verfahrenKontakt.kategorien), {
			headers: { 'Content-Type': 'application/json' },
		});
	}

	public loadKontakteInVerfahren(verfahren: VerfahrenResource): Observable<KontaktInVerfahrenListResource> {
		return this.resourceFactory.from(verfahren).get(VerfahrenLinkRel.KONTAKTE);
	}

	public adressatenColumDisplayed(show: boolean) {
		this.adressatenColDisplayed.emit(show);
	}

	public updateVerfahrenKontaktFreiText(
		verfahrenKontakt: VerfahrenKontaktResource
	): Observable<VerfahrenKontaktResource> {
		return this.resourceFactory
			.from(verfahrenKontakt)
			.patch(VerfahrenKontaktLinkRel.EDIT_FREITEXT, verfahrenKontakt);
	}

	public getAllVerfahrenForKontakt(kontakt: KontaktResource): Observable<VerfahrenListResource> {
		return this.resourceFactory.from(kontakt).get(KontaktLinkRel.VERFAHREN_BY_KONTAKT);
	}

	public getKontakteByName(
		rootResource: Resource,
		isJuristischePerson: boolean,
		firmenName: string,
		vorname: string,
		name: string
	): Observable<KontaktListResource> {
		// Format der URL sporadisch verschieden: mal als "?isJuristischePerson=false&...", mal als "{ isJuristischePerson, vorname, ...}"
		const url: string = getUrl(rootResource, ApiRootLinkRel.KONTAKTS_BY_NAME)
			.replace(/\?.*$/, '')
			.replace(/\{.*$/, '');
		const params: HttpParams = new HttpParams()
			.set('isJuristischePerson', isJuristischePerson.toString())
			.set('firmenName', firmenName)
			.set('vorname', vorname)
			.set('name', name);

		return this.httpClient.get<KontaktListResource>(url, { params });
	}
}
